import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {
  FileType,
  WorkerFile,
  getWorkerFiles,
  uploadWorkerFile,
  updateWorkerFile,
  deleteWorkerFile
} from '../../api/workerFilesApi';
import { uploadWorkerFileToEasyFlex, updateWorkerRequest } from '../../api/workersApi';
import { format } from 'date-fns';
import { FilePreviewModal } from '../WorkerFiles/FilePreviewModal';
import {
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  CloudUpload as UploadIcon,
  Sync as SyncIcon,
  Badge as BadgeIcon,
  CreditCard as LicenseIcon
} from '@mui/icons-material';

type JsonMeta = {
  schema?: 'worker-doc-v1';
  docType?: 'RIJBEWIJS' | 'ID';
  // Rijbewijs
  side?: 'FRONT' | 'BACK';
  licenseNo?: string;
  // ID
  idType?: string;
  docNumber?: string;
};

const parseMeta = (description?: string): JsonMeta => {
  if (!description) return {};
  try {
    const obj = JSON.parse(description);
    if (obj && typeof obj === 'object') return obj as JsonMeta;
  } catch { /* not JSON metadata; treat description as plain text */ }
  return {};
};

const stringifyMeta = (partial: JsonMeta): string => {
  return JSON.stringify({ schema: 'worker-doc-v1', ...partial });
};

const ID_DOC_TYPES = [
  { value: 'ID_OWN_COUNTRY', label: 'ID own country' },
  { value: 'PASSPORT_OWN_COUNTRY', label: 'Passport own country' },
  { value: 'PASSPORT_NL', label: 'Passport NL' },
  { value: 'ID_NL', label: 'ID NL' },
  { value: 'VISA', label: 'VISA' }
];

interface IdentificationDocumentsProps {
  workerId: string;
  onUpdated?: () => void;
}

const SectionHeader: React.FC<{ title: string; count?: number; icon?: React.ReactNode; color?: 'primary' | 'secondary' | 'success' | 'warning' | 'info' }> = ({ title, count, icon, color = 'primary' }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {icon}
      <Typography variant="subtitle1" color={color}>{title}</Typography>
    </Box>
    {typeof count === 'number' && (
      <Chip size="small" color={color} variant="outlined" label={count} />
    )}
  </Box>
);

/**
 * Identification documents for a worker: driving licence (rijbewijs) + ID
 * documents (passport, ID card, visa). Certifications used to live here but
 * have moved to <WorkerCertifications /> which is backed by the dedicated
 * workerCertifications table + AI extraction pipeline.
 */
export const IdentificationDocuments: React.FC<IdentificationDocumentsProps> = ({ workerId, onUpdated }) => {
  const [files, setFiles] = useState<WorkerFile[]>([]);
  const [, setLoading] = useState<boolean>(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' | 'warning' }>({ open: false, message: '', severity: 'info' });
  const [previewFile, setPreviewFile] = useState<WorkerFile | null>(null);
  const [uploadingToEasyFlexFileId, setUploadingToEasyFlexFileId] = useState<number | null>(null);

  // Rijbewijs state
  const [licenseNo, setLicenseNo] = useState<string>('');
  const rijbewijsFiles = useMemo(() => files.filter(f => f.fileType === FileType.RIJBEWIJS), [files]);
  const rijbewijsFront = useMemo(() => rijbewijsFiles.find(f => parseMeta(f.description).side === 'FRONT') || null, [rijbewijsFiles]);
  const rijbewijsBack = useMemo(() => rijbewijsFiles.find(f => parseMeta(f.description).side === 'BACK') || null, [rijbewijsFiles]);

  // ID Documents
  type IdRow = { file: WorkerFile; meta: JsonMeta };
  const idRows: IdRow[] = useMemo(() => files.filter(f => f.fileType === FileType.ID_DOCUMENT).map(f => ({ file: f, meta: parseMeta(f.description) })), [files]);
  const [idDialogOpen, setIdDialogOpen] = useState<boolean>(false);
  const [idForm, setIdForm] = useState<{ idType: string; docNumber: string; file?: File | null }>({ idType: ID_DOC_TYPES[0].value, docNumber: '', file: null });

  const showMsg = (message: string, severity: 'success' | 'error' | 'info' | 'warning' = 'info') => setSnackbar({ open: true, message, severity });

  const fetchFiles = async () => {
    setLoading(true);
    try {
      const data = await getWorkerFiles(workerId);
      setFiles(data);
      const any = data.find(f => f.fileType === FileType.RIJBEWIJS && parseMeta(f.description).licenseNo);
      setLicenseNo(any ? (parseMeta(any.description).licenseNo || '') : '');
    } catch (e: any) {
      showMsg('Failed to load documents', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchFiles(); }, [workerId]);

  const handleRijbewijsUpload = async (side: 'FRONT' | 'BACK', file: File) => {
    try {
      const description = stringifyMeta({ docType: 'RIJBEWIJS', side, licenseNo });
      await uploadWorkerFile({ workerId, file, fileType: FileType.RIJBEWIJS, description });
      showMsg(`Uploaded ${side.toLowerCase()} image`, 'success');
      await fetchFiles();
    } catch (e: any) {
      showMsg('Failed to upload driving license image', 'error');
    }
  };

  const handleRijbewijsDelete = async (wf: WorkerFile | null) => {
    if (!wf) return;
    try {
      await deleteWorkerFile(wf.id);
      showMsg('Deleted image', 'success');
      await fetchFiles();
    } catch (e: any) {
      showMsg('Failed to delete image', 'error');
    }
  };

  const handleRijbewijsSaveLicenseNo = async () => {
    try {
      const target = rijbewijsFront || rijbewijsBack;
      if (!target) { showMsg('Upload front or back first to save number', 'warning'); return; }
      const meta = parseMeta(target.description);
      const newMeta: JsonMeta = { ...meta, schema: 'worker-doc-v1', docType: 'RIJBEWIJS', licenseNo };
      await updateWorkerFile(target.id, { description: stringifyMeta(newMeta) });
      // central-db is the source of truth for EasyFlex-side fields
      // (driver's licence number etc). We no longer mirror local edits into
      // an optima-side EasyFlex row — central-db picks them up on sync.
      showMsg('Saved license number', 'success');
      await fetchFiles();
      onUpdated?.();
    } catch (e: any) {
      showMsg('Failed to save license number', 'error');
    }
  };

  const openIdDialogForNew = () => {
    setIdForm({ idType: ID_DOC_TYPES[0].value, docNumber: '', file: null });
    setIdDialogOpen(true);
  };

  // Maps the local 5-value type list down to the canonical 4-value
  // IdDocumentType enum on WorkerEntity / NAW readiness. ID_NL +
  // ID_OWN_COUNTRY both collapse to ID_CARD; PASSPORT_NL + PASSPORT_OWN_COUNTRY
  // → PASSPORT; VISA → RESIDENCE_PERMIT.
  const mapToCanonicalIdType = (t: string): 'ID_CARD' | 'PASSPORT' | 'RESIDENCE_PERMIT' | 'DRIVERS_LICENSE' => {
    switch (t) {
      case 'ID_OWN_COUNTRY':
      case 'ID_NL':
        return 'ID_CARD';
      case 'PASSPORT_OWN_COUNTRY':
      case 'PASSPORT_NL':
        return 'PASSPORT';
      case 'VISA':
        return 'RESIDENCE_PERMIT';
      default:
        return 'ID_CARD';
    }
  };

  const saveIdDialog = async () => {
    try {
      if (!idForm.file) { showMsg('Please choose a file', 'warning'); return; }
      const description = stringifyMeta({ docType: 'ID', idType: idForm.idType, docNumber: idForm.docNumber });
      await uploadWorkerFile({ workerId, file: idForm.file, fileType: FileType.ID_DOCUMENT, description });
      const canonicalType = mapToCanonicalIdType(idForm.idType);

      // ALSO write to the canonical WorkerEntity columns so NAW readiness
      // is satisfied. Without this, uploading via this component leaves
      // idDocumentType / idDocumentNumber null on the worker → NAW Generate
      // stays disabled. Best-effort: failures don't block the file upload
      // since the legacy worker_files JSON description is the historical
      // source of truth.
      try {
        await updateWorkerRequest({
          workerId,
          idDocumentType: canonicalType,
          idDocumentNumber: idForm.docNumber || null,
        });
      } catch (err) {
        console.error('Failed to write canonical idDocument* columns on worker', err);
      }

      // no local EasyFlex mirror write. The canonical worker columns
      // above (idDocumentType/idDocumentNumber) drive NAW readiness; central-db
      // owns the EasyFlex-side copy and syncs it independently.
      showMsg('ID document uploaded', 'success');
      setIdDialogOpen(false);
      await fetchFiles();
      onUpdated?.();
    } catch (e: any) {
      showMsg('Failed to save ID document', 'error');
    }
  };

  const deleteId = async (row: IdRow) => {
    try { await deleteWorkerFile(row.file.id); showMsg('Deleted ID document', 'success'); await fetchFiles(); } catch { showMsg('Failed to delete', 'error'); }
  };

  const handleUploadToEasyFlex = async (wf: WorkerFile) => {
    setUploadingToEasyFlexFileId(wf.id);
    try {
      await uploadWorkerFileToEasyFlex(workerId, wf.id);
      showMsg('Uploaded to EasyFlex', 'success');
    } catch (e: any) {
      showMsg(e?.message || 'Failed to upload to EasyFlex', 'error');
    } finally {
      setUploadingToEasyFlexFileId(null);
    }
  };

  return (
    <Card sx={{ mb: 2, borderRadius: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BadgeIcon color="primary" />
          Identification documents
        </Typography>

        {/* Rijbewijs */}
        <Box sx={{ mt: 1, mb: 2 }}>
          <SectionHeader title="Driving License (Rijbewijs)" count={rijbewijsFiles.length} icon={<LicenseIcon color="info" />} color="info" />
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Rijbewijs nummer"
                value={licenseNo}
                onChange={(e) => setLicenseNo(e.target.value)}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button variant="outlined" size="small" startIcon={<UploadIcon />} component="label">
                Upload Front
                <input type="file" accept="image/*" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) handleRijbewijsUpload('FRONT', f); }} />
              </Button>
              <Button variant="outlined" size="small" startIcon={<UploadIcon />} component="label">
                Upload Back
                <input type="file" accept="image/*" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) handleRijbewijsUpload('BACK', f); }} />
              </Button>
              <Button variant="contained" size="small" onClick={handleRijbewijsSaveLicenseNo}>Save Number</Button>
            </Grid>
          </Grid>
          <TableContainer component={Paper} sx={{ mt: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Side</TableCell>
                  <TableCell>File</TableCell>
                  <TableCell>Uploaded</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[{ label: 'Front', wf: rijbewijsFront }, { label: 'Back', wf: rijbewijsBack }].map(({ label, wf }) => (
                  <TableRow key={label}>
                    <TableCell>{label}</TableCell>
                    <TableCell>{wf ? wf.originalFileName : <Typography color="text.secondary">Not uploaded</Typography>}</TableCell>
                    <TableCell>{wf ? format(new Date(wf.createdAt), 'yyyy-MM-dd') : '-'}</TableCell>
                    <TableCell align="right">
                      {wf && (
                        <>
                          <Tooltip title="Preview">
                            <IconButton size="small" onClick={() => setPreviewFile(wf)}><ViewIcon /></IconButton>
                          </Tooltip>
                          <Tooltip title="Upload to EasyFlex">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleUploadToEasyFlex(wf)}
                                disabled={uploadingToEasyFlexFileId === wf.id}
                              >
                                {uploadingToEasyFlexFileId === wf.id ? <CircularProgress size={18} /> : <SyncIcon />}
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" onClick={() => handleRijbewijsDelete(wf)}><DeleteIcon /></IconButton>
                          </Tooltip>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* ID Documents */}
        <Box sx={{ mt: 3 }}>
          <SectionHeader title="ID Documents" count={idRows.length} icon={<BadgeIcon color="secondary" />} color="secondary" />
          <Box sx={{ mb: 1 }}>
            <Button size="small" variant="outlined" startIcon={<UploadIcon />} onClick={openIdDialogForNew}>Add ID Document</Button>
          </Box>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell>Document Number</TableCell>
                  <TableCell>File</TableCell>
                  <TableCell>Uploaded</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {idRows.map((row) => (
                  <TableRow key={row.file.id}>
                    <TableCell>{ID_DOC_TYPES.find(t => t.value === row.meta.idType)?.label || '-'}</TableCell>
                    <TableCell>{row.meta.docNumber || '-'}</TableCell>
                    <TableCell>{row.file.originalFileName}</TableCell>
                    <TableCell>{format(new Date(row.file.createdAt), 'yyyy-MM-dd')}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Preview"><IconButton size="small" onClick={() => setPreviewFile(row.file)}><ViewIcon /></IconButton></Tooltip>
                      <Tooltip title="Upload to EasyFlex">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleUploadToEasyFlex(row.file)}
                            disabled={uploadingToEasyFlexFileId === row.file.id}
                          >
                            {uploadingToEasyFlexFileId === row.file.id ? <CircularProgress size={18} /> : <SyncIcon />}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Delete"><IconButton size="small" onClick={() => deleteId(row)}><DeleteIcon /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {idRows.length === 0 && (
                  <TableRow><TableCell colSpan={5}><Typography color="text.secondary">No ID documents added</Typography></TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {previewFile && (<FilePreviewModal file={previewFile} open={true} onClose={() => setPreviewFile(null)} />)}

        <Dialog open={idDialogOpen} onClose={() => setIdDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Add ID Document</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Type</InputLabel>
                <Select value={idForm.idType} label="Type" onChange={(e) => setIdForm({ ...idForm, idType: e.target.value as string })}>
                  {ID_DOC_TYPES.map((t) => (<MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>))}
                </Select>
              </FormControl>
              <TextField label="Document Number" value={idForm.docNumber} onChange={(e) => setIdForm({ ...idForm, docNumber: e.target.value })} fullWidth size="small" />
              <Button variant="outlined" startIcon={<UploadIcon />} component="label">
                Select File (PDF/Image)
                <input type="file" hidden accept="application/pdf,image/*" onChange={(e) => setIdForm({ ...idForm, file: e.target.files?.[0] })} />
              </Button>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIdDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={saveIdDialog}>Upload</Button>
          </DialogActions>
        </Dialog>

        <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
          <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </CardContent>
    </Card>
  );
};

export default IdentificationDocuments;
